import { Component, Inject, Injector, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Store } from '@ngrx/store';
import { snakeCase } from 'lodash';
import { take } from 'rxjs/operators';
import { BackgroundJob } from 'Src/ng2/shared/services/background-job/background-job.service';
import { getSchool } from '../../../store/selectors/school-selectors';
import { pdfReportTypes, TValidPdfReportKeys, TValidPdfReportNames } from '../../constants/pdf-reports.constant';
import { ImSchool } from '../../services/im-models/im-school';
import { UtilitiesService } from '../../services/utilities/utilities.service';
import { BaseModalComponent } from '../base-modal.component';
import { IBaseModalData } from '../modals.service';

const State = {
  NOT_STARTED: 'NOT_STARTED',
  GENERATING: 'GENERATING',
  READY: 'READY',
};

type TValidSpinnerModes = 'determinate' | 'indeterminate';
@Component({
  selector: 'student-report-modal',
  template: require('./student-report.component.html'),
})
export class StudentReportModalComponent extends BaseModalComponent implements OnInit {
  report;
  state: string;
  selectedReportName: TValidPdfReportNames;
  selectedStudentIds: string[];
  downloadUrl: string;
  mode: TValidSpinnerModes;
  fileName: string;
  underStudentLimit: boolean;
  isHsSchool: boolean = false;
  isEmsSchool: boolean = false;

  title: string;
  itemCount: number;
  itemType: string;
  isProfileMode: boolean = false;

  constructor(
    dialogRef: MatDialogRef<StudentReportModalComponent>,
    private store: Store<any>,
    private utils: UtilitiesService,
    private imSchool: ImSchool,
    private injector: Injector,
    @Inject('ErrorModal') public ErrorModal,
    @Inject('Report') public Report,
    @Inject('$window') private $window,
    @Inject(MAT_DIALOG_DATA) public data: IBaseModalData,
  ) {
    super(dialogRef);
    this.selectedStudentIds = this.data.studentIds;
    this.underStudentLimit = this.selectedStudentIds.length <= 250;
    this.isProfileMode = this.data.isProfileMode;
    this.state = State.NOT_STARTED;
    this.selectedReportName = pdfReportTypes[`snapshot`];
    this.mode = 'indeterminate';
    this.title = 'Generate report';
  }

  ngOnInit(): void {
    this.store
      .select(getSchool)
      .pipe(take(1))
      .subscribe(school => {
        if (this.imSchool.isHighSchool(school)) this.isHsSchool = true;
        if (this.imSchool.isEmsSchoool(school)) this.isEmsSchool = true;
      });
    this.setSelectedReport('snapshot', this.selectedStudentIds);
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
  }

  private getReport(reportName: TValidPdfReportKeys, studentIds: string[], userProvidedFilename: string) {
    return new this.Report({
      _id: this.utils.createV4Uuid(),
      reportName,
      studentIds,
      userProvidedFilename,
    });
  }

  private handleError(error: Error): void {
    const payload = { message: 'Error generating student report', error };
    super.onError(payload);
  }

  setSelectedReport(reportName: TValidPdfReportKeys, selectedStudentIds: string[]): void {
    this.selectedReportName = pdfReportTypes[reportName];
    this.itemCount = selectedStudentIds.length;
    const uniqueFileName = reportName === 'regentsSched' ? 'regentsPrep' : reportName;
    this.fileName = `students_${snakeCase(uniqueFileName)}`;
    this.report = this.getReport(reportName, selectedStudentIds, this.fileName);
    this.itemType = 'student';
  }

  generate(): void {
    this.state = State.GENERATING;
    this.report.$create((report, headers) => {
      const backgroundJob = this.injector.get(BackgroundJob) as any;
      const backgroundJobPromise = backgroundJob(headers('nv-background-jobs')).promise();
      backgroundJobPromise.then(
        () => {
          this.report.$get(report => {
            this.state = State.READY;
            this.downloadUrl = report._downloadUrl;
          });
          backgroundJobPromise.catch(error => {
            this.handleError(error);
          });
        },
        error => {
          this.handleError(error);
        },
      );
    });
  }

  download(): void {
    this.$window.open(this.downloadUrl);
    super.close({ action: 'DOWNLOADED' });
  }

  cancel(): void {
    super.close();
  }
}
